import { ITextWidgetData, ITextWidgetStyle, TextWidgetData } from "@pageBuilder/typings";
import { CssProperties } from "../../../models/css-property";
import { BackgroundDataRenderer } from "../../../shared/rendering/background-data-renderer";
import { WidgetRendererBase } from "../widget-renderer-base";
import { TextScriptRenderer } from "./text-script.renderer";
import { IRenderingCommonContext } from "@commonRendering/types";
import { VerticalAlignmentRenderer } from "../vertical-alignment-renderer";

export class TextWidgetRenderer extends WidgetRendererBase<ITextWidgetData> {

    private readonly backgroundRenderer: BackgroundDataRenderer;
    private readonly backgroundClassName = "widget-background";
    private readonly backgroundSelector = `.widget-content .${this.backgroundClassName}`;
    private readonly scriptRenderer: TextScriptRenderer;
    private readonly verticalAlignmentRenderer: VerticalAlignmentRenderer;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.backgroundRenderer = new BackgroundDataRenderer(this.styleService);
        this.scriptRenderer = new TextScriptRenderer();
        this.verticalAlignmentRenderer = new VerticalAlignmentRenderer();
    }

    protected createNewInternal(): HTMLElement {
        return this.createWidgetElement("textWidget");
    }

    protected renderInternal(element: HTMLElement, data: ITextWidgetData): HTMLElement {
        this.ensureBackground(element);
        this.renderStyles(element, data);
        this.backgroundRenderer.renderBackground(element, data.background, this.backgroundSelector, "text widget background");
        const inlineElement = this.ensureInlineElement(element);
        if (data.html) {
            const sanitizedHtml = data.html;
            inlineElement.innerHTML = sanitizedHtml;
            element.setAttribute("data-html", sanitizedHtml);
        }

        data.style?.minHeight
            ? element.setAttribute("minHeight", data.style?.minHeight.toString())
            : element.setAttribute("minHeight", TextWidgetData.defaultStyle.minHeight.toString());

        this.scriptRenderer.renderCommon(this.renderedDocument);
        this.scriptRenderer.renderLocal(element);

        return element;
    }

    private ensureBackground(element: HTMLElement): void {
        const background = element.querySelector(this.backgroundSelector);
        if (!background) {
            const content = element.querySelector(".widget-content");

            const background = document.createElement("div");
            background.classList.add(this.backgroundClassName);

            content?.append(background);
        }
    }

    private ensureInlineElement(element: HTMLElement): HTMLDivElement {
        let inlineElement = element.querySelector<HTMLDivElement>("[data-inline-inject]");
        if (!inlineElement) {
            const content = element.querySelector(".widget-content") as HTMLElement;
            inlineElement = document.createElement("div");
            inlineElement.setAttribute("data-inline-inject", "");
            inlineElement.classList.toggle("ck-content", true);
            content.insertAdjacentElement("afterbegin", inlineElement);
        }

        return inlineElement;
    }

    protected renderStyles(element: HTMLElement, data: ITextWidgetData): void {
        if (!data.style) {
            this.cleanupStyles(element);
            return;
        }

        this.renderBaseStyles(element, data.style);
        this.renderContentStyles(element, data.style);
        this.renderBackgroundStyles(element, data.style);
    }

    private renderBaseStyles(element: HTMLElement, style: ITextWidgetStyle): void {
        this.animationRenderer.setAnimationStyles(element, style.animation);
        element.style.minHeight = style.minHeight ? `${style.minHeight}px` : "auto";
    }

    private renderContentStyles(element: HTMLElement, style: ITextWidgetStyle): void {
        const contentProps = new CssProperties();

        if (style.padding) {
            contentProps.tryAdd("padding",`${style.padding.toString()}px`);
        }

        if (style.borderRadius) {
            contentProps.tryAdd("border-top-left-radius", `${style.borderRadius.topLeftPx}px`);
            contentProps.tryAdd("border-top-right-radius", `${style.borderRadius.topRightPx}px`);
            contentProps.tryAdd("border-bottom-left-radius", `${style.borderRadius.bottomLeftPx}px`);
            contentProps.tryAdd("border-bottom-right-radius", `${style.borderRadius.bottomRightPx}px`);
        }

        const alignItems = this.verticalAlignmentRenderer.getAlignmentBySelfPosition(style.verticalAlignment);
        contentProps.tryAdd("align-content", alignItems);

        this.styleService.setStyle(element, `#${element.id} .widget-content`, contentProps.data);
    }

    private renderBackgroundStyles(element: HTMLElement, style: ITextWidgetStyle): void {
        const backgroundProps = new CssProperties();

        if (style.thickness) {
            backgroundProps.tryAdd("border-width", `${style.thickness.toString()}px`);
            backgroundProps.tryAdd("border-style", "solid");

            if (style.borderColor) {
                backgroundProps.tryAdd("border-color", style.borderColor);
            }
        }

        this.styleService.setStyle(element, `#${element.id} .widget-content > .widget-background`, backgroundProps.data);
    }

    private cleanupStyles(element: HTMLElement): void {
        super.cleanupAllElementStyles(element);
        this.backgroundRenderer.cleanupMediaBackgroundContent(element, this.backgroundSelector);
    }

    private getBaseElementSelectors(element: HTMLElement): string [] {
        return [`#${element.id}`, `#${element.id} .widget-content`, `#${element.id} .widget-content > .widget-background`];
    }

    protected getContentSelectorsInternal(element: HTMLElement): string[] {
        const selectors = this.getBaseElementSelectors(element);
        selectors.push(...this.backgroundRenderer.getContentSelectorsInternal(element, this.backgroundSelector));
        return selectors;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        //nothing
    }

}
